import { Language } from '../constants/language';
import { Currency } from '../constants/currency';
import { StorageUtil } from '../utils/storage-util';

export interface AppConfiguration {
  language: Language;
  currency: Currency;
  region: string;
}

class ConfigurationService {
  private static readonly DEFAULT_CONFIG: AppConfiguration = {
    language: Language.AR,
    currency: Currency.EGP,
    region: 'EG' // Egypt
  };

  private config: AppConfiguration;
  private listeners: ((config: AppConfiguration) => void)[] = [];

  constructor() {
    this.config = this.loadConfiguration();
  }

  /**
   * Load configuration from cache or set defaults
   */
  private loadConfiguration(): AppConfiguration {
    try {
      // Try to load from localStorage using StorageUtil
      const cachedConfig = StorageUtil.getConfig<AppConfiguration | null>(
        StorageUtil.KEYS.APP_CONFIG, 
        null
      );
      
      if (cachedConfig) {
        return {
          language: cachedConfig.language || ConfigurationService.DEFAULT_CONFIG.language,
          currency: cachedConfig.currency || ConfigurationService.DEFAULT_CONFIG.currency,
          region: cachedConfig.region || ConfigurationService.DEFAULT_CONFIG.region
        };
      }

      // Load from individual storage keys for backward compatibility
      const language = StorageUtil.getString(StorageUtil.KEYS.LANGUAGE, ConfigurationService.DEFAULT_CONFIG.language) as Language;
      const currency = StorageUtil.getString(StorageUtil.KEYS.CURRENCY, ConfigurationService.DEFAULT_CONFIG.currency) as Currency;
      const region = StorageUtil.getString(StorageUtil.KEYS.REGION, ConfigurationService.DEFAULT_CONFIG.region);

      const config: AppConfiguration = {
        language,
        currency, 
        region
      };

      // Save the consolidated config
      this.saveConfiguration(config);
      return config;

    } catch (error) {
      console.warn('Failed to load configuration from cache, using defaults:', error);
      return { ...ConfigurationService.DEFAULT_CONFIG };
    }
  }

  /**
   * Save configuration to cache
   */
  private saveConfiguration(config: AppConfiguration): void {
    try {
      StorageUtil.setConfig(StorageUtil.KEYS.APP_CONFIG, config);
      // Also save individual keys for backward compatibility
      StorageUtil.setConfig(StorageUtil.KEYS.LANGUAGE, config.language);
      StorageUtil.setConfig(StorageUtil.KEYS.CURRENCY, config.currency);
      StorageUtil.setConfig(StorageUtil.KEYS.REGION, config.region);
    } catch (error) {
      console.warn('Failed to save configuration to cache:', error);
    }
  }

  /**
   * Get current configuration
   */
  public getConfiguration(): AppConfiguration {
    return { ...this.config };
  }

  /**
   * Get current language
   */
  public getLanguage(): Language {
    return this.config.language;
  }

  /**
   * Get current currency
   */
  public getCurrency(): Currency {
    return this.config.currency;
  }

  /**
   * Get current region
   */
  public getRegion(): string {
    return this.config.region;
  }

  /**
   * Update language and notify listeners
   */
  public setLanguage(language: Language): void {
    const newConfig = {
      ...this.config,
      language
    };
    
    this.config = newConfig;
    this.saveConfiguration(newConfig);
    this.notifyListeners(newConfig);
  }

  /**
   * Update currency and notify listeners
   */
  public setCurrency(currency: Currency): void {
    const newConfig = {
      ...this.config,
      currency
    };
    
    this.config = newConfig;
    this.saveConfiguration(newConfig);
    this.notifyListeners(newConfig);
  }

  /**
   * Update region and notify listeners
   */
  public setRegion(region: string): void {
    const newConfig = {
      ...this.config,
      region
    };
    
    this.config = newConfig;
    this.saveConfiguration(newConfig);
    this.notifyListeners(newConfig);
  }

  /**
   * Update full configuration
   */
  public setConfiguration(config: Partial<AppConfiguration>): void {
    const newConfig = {
      ...this.config,
      ...config
    };
    
    this.config = newConfig;
    this.saveConfiguration(newConfig);
    this.notifyListeners(newConfig);
  }

  /**
   * Subscribe to configuration changes
   */
  public subscribe(listener: (config: AppConfiguration) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of configuration changes
   */
  private notifyListeners(config: AppConfiguration): void {
    this.listeners.forEach(listener => {
      try {
        listener(config);
      } catch (error) {
        console.warn('Error in configuration listener:', error);
      }
    });
  }

  /**
   * Initialize configuration on app startup
   */
  public initialize(): void {
    // Auto-detect region if not set (optional enhancement)
    if (this.config.region === ConfigurationService.DEFAULT_CONFIG.region) {
      this.detectRegion();
    }
  }

  /**
   * Auto-detect user's region (basic implementation)
   */
  private detectRegion(): void {
    try {
      // Use browser language to infer region
      const browserLang = navigator.language || navigator.languages?.[0];
      if (browserLang) {
        const region = browserLang.split('-')[1]?.toUpperCase() || 'EG';
        if (region !== this.config.region) {
          this.setRegion(region);
        }
      }
    } catch (error) {
      console.warn('Failed to detect region:', error);
    }
  }

  /**
   * Clear all cached configuration
   */
  public clearCache(): void {
    try {
      StorageUtil.removeBatch([
        StorageUtil.KEYS.APP_CONFIG,
        StorageUtil.KEYS.LANGUAGE,
        StorageUtil.KEYS.CURRENCY,
        StorageUtil.KEYS.REGION
      ]);
      
      this.config = { ...ConfigurationService.DEFAULT_CONFIG };
      this.notifyListeners(this.config);
    } catch (error) {
      console.warn('Failed to clear configuration cache:', error);
    }
  }
}

export const configurationService = new ConfigurationService();